import {FONTS, ROLLUPS, SECTION_TYPES, THEMES} from './quoteTemplateSchema';
import type {PlateValue, QuoteSection, QuoteSectionType, QuoteTemplate} from '../resolver/types';

const isObject = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null && !Array.isArray(value);

const bool = (value: unknown, fallback: boolean): boolean => (typeof value === 'boolean' ? value : fallback);

const oneOf = <T extends string>(value: unknown, allowed: readonly T[], fallback: T): T =>
	typeof value === 'string' && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;

/** Only a normalised six-digit hex reaches the renderers; anything else would print as a broken colour. */
const hex = (value: unknown, fallback: string): string =>
	typeof value === 'string' && /^#[0-9a-fA-F]{6}$/.test(value) ? value.toLowerCase() : fallback;

const plate = (value: unknown, fallback: PlateValue): PlateValue => (Array.isArray(value) ? (value as PlateValue) : fallback);

const normalizeSection = (raw: unknown, fallback: QuoteSection): QuoteSection => {
	if (!isObject(raw)) return fallback;
	return {
		type: fallback.type,
		enabled: bool(raw.enabled, fallback.enabled),
		// A non-string title is not an override. null means "use the default label for this locale",
		// which is also the right answer for a value we cannot read.
		title: typeof raw.title === 'string' && raw.title.trim() !== '' ? raw.title : null,
		sourceBlockId: typeof raw.sourceBlockId === 'string' ? raw.sourceBlockId : null,
		body: plate(raw.body, fallback.body),
	};
};

/**
 * Total `unknown -> QuoteTemplate`. Unknown root keys, unknown domain keys and unknown section
 * types are dropped; an unknown enum value falls back to its domain default; a missing key comes
 * from the fallback. Nothing here throws, so the designer always opens.
 */
export function normalizeTemplate(raw: unknown, fallback: QuoteTemplate): QuoteTemplate {
	if (!isObject(raw)) return fallback;

	const branding = isObject(raw.branding) ? raw.branding : {};
	const layout = isObject(raw.layout) ? raw.layout : {};
	const estimate = isObject(raw.estimate) ? raw.estimate : {};
	const columns = isObject(estimate.columns) ? estimate.columns : {};
	const grouping = isObject(estimate.grouping) ? estimate.grouping : {};
	const totals = isObject(estimate.totals) ? estimate.totals : {};
	const rawSections = Array.isArray(raw.sections) ? raw.sections : [];

	const sectionByType = new Map<QuoteSectionType, unknown>();
	for (const entry of rawSections) {
		if (isObject(entry) && (SECTION_TYPES as readonly string[]).includes(entry.type as string)) {
			sectionByType.set(entry.type as QuoteSectionType, entry);
		}
	}

	return {
		schemaVersion: fallback.schemaVersion,
		branding: {
			accentColor: hex(branding.accentColor, fallback.branding.accentColor),
			font: oneOf(branding.font, FONTS, fallback.branding.font),
			showLogo: bool(branding.showLogo, fallback.branding.showLogo),
		},
		layout: {
			theme: oneOf(layout.theme, THEMES, fallback.layout.theme),
			pageHeader: bool(layout.pageHeader, fallback.layout.pageHeader),
			pageFooter: bool(layout.pageFooter, fallback.layout.pageFooter),
		},
		estimate: {
			rollup: oneOf(estimate.rollup, ROLLUPS, fallback.estimate.rollup),
			columns: {
				time: bool(columns.time, fallback.estimate.columns.time),
				rate: bool(columns.rate, fallback.estimate.columns.rate),
				amount: bool(columns.amount, fallback.estimate.columns.amount),
			},
			grouping: {
				roleTotals: bool(grouping.roleTotals, fallback.estimate.grouping.roleTotals),
				phaseSubtotals: bool(grouping.phaseSubtotals, fallback.estimate.grouping.phaseSubtotals),
				phaseExpenses: bool(grouping.phaseExpenses, fallback.estimate.grouping.phaseExpenses),
				deliverableHeaders: bool(grouping.deliverableHeaders, fallback.estimate.grouping.deliverableHeaders),
			},
			totals: {
				subtotal: bool(totals.subtotal, fallback.estimate.totals.subtotal),
				expenses: bool(totals.expenses, fallback.estimate.totals.expenses),
				grandTotal: bool(totals.grandTotal, fallback.estimate.totals.grandTotal),
			},
		},
		// Rebuilt from SECTION_TYPES rather than from the payload, so the result always carries every
		// section this build knows about, in a stable order, whatever the row held.
		sections: SECTION_TYPES.map(type => {
			const fallbackSection =
				fallback.sections.find(section => section.type === type) ??
				({type, enabled: true, title: null, sourceBlockId: null, body: []} as QuoteSection);
			return normalizeSection(sectionByType.get(type), fallbackSection);
		}),
	};
}
